import { useId } from 'react'
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'

/**
 * The one control that does not use `Field`.
 *
 * A checkbox's label belongs beside the box, not stacked above it — a
 * label above a checkbox reads as a heading for a group, which is what
 * `<fieldset>`/`<legend>` are for. So this owns its own label, hint and
 * `aria-describedby` rather than borrowing `Field`'s stacked layout.
 *
 * The whole label is the hit target (the `<input>` is inside it), which
 * matters more here than anywhere else: a 13px checkbox is a small thing
 * to hit, and this app has rows of them.
 */

export interface CheckboxProps extends Omit<
  ComponentPropsWithoutRef<'input'>,
  'className' | 'type'
> {
  label: ReactNode
  /** Static help, rendered under the label and wired with
   * `aria-describedby`. */
  hint?: ReactNode
}

export function Checkbox({
  label,
  hint,
  ...rest
}: CheckboxProps): ReactElement {
  const id = useId()
  const hintId = `${id}-hint`
  const hasHint = hint !== undefined

  return (
    <div className="flex flex-col gap-0.5">
      <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
        <input
          type="checkbox"
          aria-describedby={hasHint ? hintId : undefined}
          // `accent-accent` is how a native checkbox is tinted; without it
          // the box stays the browser's blue and is the one control on
          // screen that ignores the theme.
          className="h-3.5 w-3.5 shrink-0 accent-accent disabled:cursor-not-allowed disabled:opacity-50"
          {...rest}
        />
        {label}
      </label>
      {hasHint && (
        // Indented to clear the box so the hint reads as belonging to the
        // label rather than to the next control down.
        <p id={hintId} className="pl-5.5 text-xs text-faint">
          {hint}
        </p>
      )}
    </div>
  )
}
